import { v4 as uuidv4 } from "uuid";
import { getApiErrorMessage } from "./apiErrorMessage";
import { getAllProviderUsersByProviderId, getProviderUserByEmail } from "../api/api-methods";
import { applyContactDesignations } from "./applyContactDesignations";
import {
  getDesignationsForNewVendorContact,
  getEligibleCategoriesForNewVendorRequest,
} from "./requestContactDesignations";

async function findExistingProviderUser(providerId, email) {
  const normalizedEmail = email?.trim()?.toLowerCase();
  if (!normalizedEmail || !providerId) return null;

  try {
    const byEmail = await getProviderUserByEmail(normalizedEmail);
    if (
      byEmail?.provider_user_id &&
      String(byEmail.provider_id).toLowerCase() === String(providerId).toLowerCase()
    ) {
      return byEmail;
    }
  } catch {
    // fall through to list lookup
  }

  try {
    const usersData = await getAllProviderUsersByProviderId(providerId, 100, 1, "");
    return (
      usersData?.items?.find((u) => u.email?.toLowerCase() === normalizedEmail) ?? null
    );
  } catch {
    return null;
  }
}

/**
 * Creates a vendor org contact (if needed) and applies VQQ/Assessment designations
 * for the New Request Vendor flow.
 */
export async function ensureProviderContactAndDesignate({
  providerId,
  organization_id,
  providerName,
  providerDataRoles,
  contact,
  isPrimary,
  showRfiSection,
  showAssessmentSection,
  addProviderContact,
}) {
  if (!contact?.email?.trim() || !contact?.first_name?.trim()) return null;

  const provider_user_id = contact.provider_user_id || uuidv4();
  const payload = {
    provider_user_id,
    provider_id: providerId,
    organization_id,
    email: contact.email.trim(),
    first_name: contact.first_name.trim(),
    last_name: (contact.last_name || "").trim() || "lastname",
    job_title: contact.job_title || contact.title || "",
    is_primary: isPrimary,
    provider_name: providerName || "",
    ...providerDataRoles,
  };

  const existing = await findExistingProviderUser(providerId, payload.email);
  let resolvedUserId = existing?.provider_user_id;

  if (!resolvedUserId) {
    await addProviderContact(payload);
    resolvedUserId = provider_user_id;
  }

  const eligibleCategories = getEligibleCategoriesForNewVendorRequest(
    showRfiSection,
    showAssessmentSection,
  );
  const { rfiDesignation, assessmentDesignation } = getDesignationsForNewVendorContact(
    isPrimary,
    showRfiSection,
    showAssessmentSection,
  );

  if (eligibleCategories.length > 0) {
    await applyContactDesignations({
      provider_user_id: resolvedUserId,
      provider_id: providerId,
      rfiDesignation,
      assessmentDesignation,
      eligibleCategories,
    });
  }

  return resolvedUserId;
}

/**
 * Creates and designates the vendor's contacts. Each contact stands on its own: one that cannot
 * be added does not take the others down with it. The vendor is never unwound here, because there
 * is no way to delete the HyperComply organization behind it; the caller reports the failures.
 *
 * @returns {Promise<{failures: Array<{email: string, error: unknown}>}>}
 */
export async function createAndDesignateNewVendorContacts({
  providerId,
  organization_id,
  providerName,
  providerDataRoles,
  preferredContact,
  additionalContacts = [],
  showRfiSection,
  showAssessmentSection,
  addProviderContact,
}) {
  const entries = [];

  if (preferredContact?.email?.trim() && preferredContact?.first_name?.trim()) {
    entries.push({
      email: preferredContact.email.trim(),
      contact: preferredContact,
      isPrimary: true,
    });
  }

  for (const contact of additionalContacts) {
    if (!contact?.email?.trim() || !contact?.firstName?.trim()) continue;
    entries.push({
      email: contact.email.trim(),
      contact: {
        provider_user_id: uuidv4(),
        email: contact.email,
        first_name: contact.firstName,
        last_name: contact.lastName,
        job_title: contact.title,
      },
      isPrimary: false,
    });
  }

  const results = await Promise.allSettled(
    entries.map((entry) =>
      ensureProviderContactAndDesignate({
        providerId,
        organization_id,
        providerName,
        providerDataRoles,
        contact: entry.contact,
        isPrimary: entry.isPrimary,
        showRfiSection,
        showAssessmentSection,
        addProviderContact,
      }),
    ),
  );

  const failures = results
    .map((result, index) => ({ result, email: entries[index].email }))
    .filter(({ result }) => result.status === "rejected")
    .map(({ result, email }) => ({ email, error: result.reason }));

  return { failures };
}

/**
 * One sentence per contact that could not be added, for the screen to show after the vendor
 * itself was created.
 *
 * @param {Array<{email: string, error: unknown}>} failures
 * @returns {string}
 */
export function describeNewVendorContactFailures(failures) {
  return failures
    .map(
      ({ email, error }) =>
        `Vendor created. ${email} was not added: ${getApiErrorMessage(
          error,
          "something went wrong.",
        )}`,
    )
    .join(" ");
}
